import { BeatConfig, PhaseKind } from '../data/protocols';
import { breathEnvelope, pitchEnvelope, cueFrequency } from './protocolSequencer';
import { voiceClipFor } from './audioMix';
import { VoiceClip } from '../data/soundscapes';

// How far the tone glides up at a full inhale, as a fraction of the carrier.
// ~0.06 ≈ one semitone: clearly audible but gentle. The same delta is added to
// both ears so the binaural beat (their difference) is preserved exactly.
const PITCH_RISE_RATIO = 0.06;

// All guidance — spoken cues, tone cues, counting ticks and bells — runs
// through one gain bus, so a single user setting controls how loud the guidance
// sits over the background music. Voice clips are peak-normalised to -1 dBFS
// (~0.89), and 0.89 * CUE_MAX * 0.5 (master) stays just under 1.0: even the
// maximum setting cannot clip.
const CUE_MAX = 2.2;
// Between the original quiet cues (~-10 dBFS) and the full-lift version
// (~-1 dBFS), leaning louder: 0.89 * (0.75 * 2.2) * 0.5 ~ -2.7 dBFS.
const DEFAULT_CUE_VOLUME = 0.75;
const clamp01 = (n: number): number => Math.min(Math.max(n, 0), 1);

export interface AudioExtras {
  // Multiplier for the binaural bed when a run-level background is active
  // (so the bed sits beneath it). The background itself is owned by
  // ProtocolPlayer and plays continuously across the whole run.
  bedScale?: number;
  // 0..1 — how loud the guidance bus (cues, counting, bells) sits over the
  // background. Defaults to DEFAULT_CUE_VOLUME when unset.
  cueVolume?: number;
  cueMode?: 'tone' | 'voice';
  voiceUrls?: Record<VoiceClip, string>;
  counting?: 'off' | 'count' | 'countWarn';
}

export class ProtocolAudio {
  private ctx: AudioContext | null = null;
  private leftOsc?: OscillatorNode;
  private rightOsc?: OscillatorNode;
  private beatGain?: GainNode;
  private master?: GainNode;
  private cueBus?: GainNode;
  private carrierHz = 0;
  private beatHz = 0;
  private voiceBuffers: Partial<Record<VoiceClip, AudioBuffer>> = {};
  private voiceLoads: Partial<Record<VoiceClip, Promise<void>>> = {};
  private bedScale = 1;
  private paused = false;
  private ownsCtx = false;
  private suspendTimer: number | null = null;
  private readonly extras: AudioExtras;
  private readonly soundEnabled: boolean;
  private readonly cuesEnabled: boolean;
  private readonly volume: number;
  private readonly sharedCtx: AudioContext | null;

  constructor(
    soundEnabled: boolean,
    volume: number,
    cuesEnabled: boolean = false,
    extras: AudioExtras = {},
    sharedCtx: AudioContext | null = null
  ) {
    this.soundEnabled = soundEnabled;
    this.cuesEnabled = cuesEnabled;
    this.volume = volume;
    this.extras = extras;
    this.bedScale = extras.bedScale ?? 1;
    this.sharedCtx = sharedCtx;
  }

  start(config: BeatConfig): void {
    const wantsCounting = !!this.extras.counting && this.extras.counting !== 'off';
    if ((!this.soundEnabled && !this.cuesEnabled && !wantsCounting) || this.ctx) return;

    // Reuse the run-level context when one is supplied. It is created and
    // unlocked by the user's start tap; creating one here instead would happen
    // after the transition buffer — outside any user gesture — and iOS/Safari
    // would leave it suspended, silencing cues and counting for the whole run.
    const reusable = this.sharedCtx && this.sharedCtx.state !== 'closed' ? this.sharedCtx : null;
    const ctx = reusable ?? new (window.AudioContext || (window as any).webkitAudioContext)();
    this.ownsCtx = !reusable;
    this.ctx = ctx;
    this.carrierHz = config.carrierHz;
    this.beatHz = config.beatHz;

    if (ctx.state === 'suspended') {
      void ctx.resume().catch(() => {});
    }

    const master = ctx.createGain();
    master.gain.value = this.volume;
    master.connect(ctx.destination);
    this.master = master;

    // Guidance bus (see CUE_MAX): everything the user listens *for* — spoken
    // cues, tone cues, counting ticks, bells — routes through here.
    const cueBus = ctx.createGain();
    cueBus.gain.value = clamp01(this.extras.cueVolume ?? DEFAULT_CUE_VOLUME) * CUE_MAX;
    cueBus.connect(master);
    this.cueBus = cueBus;

    // The binaural bed exists only when the tonal sound is enabled; cues can
    // play through `master` without it.
    if (this.soundEnabled) {
      const beatGain = ctx.createGain();
      beatGain.gain.value = 0.02;
      beatGain.connect(master);
      this.beatGain = beatGain;

      const merger = ctx.createChannelMerger(2);
      merger.connect(beatGain);

      const left = ctx.createOscillator();
      left.type = 'sine';
      left.frequency.value = config.carrierHz;
      left.connect(merger, 0, 0);
      left.start();
      this.leftOsc = left;

      const right = ctx.createOscillator();
      right.type = 'sine';
      right.frequency.value = config.carrierHz + config.beatHz;
      right.connect(merger, 0, 1);
      right.start();
      this.rightOsc = right;
    }

    // Pre-load voice clips so cues can play instantly.
    if (this.extras.cueMode === 'voice' && this.extras.voiceUrls) {
      for (const clip of ['inhale', 'exhale', 'hold'] as VoiceClip[]) {
        const url = this.extras.voiceUrls[clip];
        // Keep the in-flight promise so a cue firing before decode completes
        // can play the clip as soon as it lands (see cue()).
        if (url) this.voiceLoads[clip] = this.loadVoice(ctx, clip, url);
      }
    }

    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'Protokoll',
        artist: 'Breathe to Become',
        album: 'Tilstanden',
      });
    }
  }

  private async loadVoice(ctx: AudioContext, clip: VoiceClip, url: string): Promise<void> {
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      this.voiceBuffers[clip] = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch {
      /* missing clip: cue() falls back to the tone */
    }
  }

  setPhase(kind: PhaseKind, progress: number): void {
    if (!this.ctx || !this.beatGain) return;
    const now = this.ctx.currentTime;

    // Volume swells with the breath.
    const target = breathEnvelope(kind, progress) * this.bedScale;
    const g = this.beatGain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(target, now + 0.05);

    // Pitch glides with the breath: rising on inhale, high at the top hold,
    // falling on exhale, low at the empty hold. The same rise is added to both
    // oscillators, so the binaural beat (their difference) stays constant.
    if (this.leftOsc && this.rightOsc) {
      const rise = this.carrierHz * PITCH_RISE_RATIO * pitchEnvelope(kind, progress);
      this.rampFrequency(this.leftOsc.frequency, this.carrierHz + rise, now);
      this.rampFrequency(this.rightOsc.frequency, this.carrierHz + this.beatHz + rise, now);
    }
  }

  private rampFrequency(param: AudioParam, target: number, now: number): void {
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(target, now + 0.08);
  }

  // Soft marker for a transition (settle / guided phase change / completion).
  // It is not part of the solfeggio bed, so it must NOT depend on
  // `soundEnabled` — otherwise it goes silent for everyone on the default
  // setup (bed off), taking the "breathe normally" cue with it.
  bell(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 320;
    osc.connect(g);
    g.connect(this.cueBus ?? this.master);
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.15, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
    osc.start(now);
    osc.stop(now + 0.85);
  }

  // Play a decoded voice clip, lifted above the background bed so the spoken
  // cue stays clearly audible over it.
  private playVoice(buf: AudioBuffer): void {
    if (!this.ctx || !this.cueBus) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.cueBus);
    src.start();
  }

  cue(kind: PhaseKind): void {
    if (!this.ctx || !this.master || !this.cuesEnabled) return;

    // Voice mode: play the decoded clip if we have it.
    if (this.extras.cueMode === 'voice') {
      const clip = voiceClipFor(kind);
      const buf = this.voiceBuffers[clip];
      if (buf) {
        this.playVoice(buf);
        return;
      }
      // Not decoded yet. The very first cue of a segment fires before the
      // preload finishes, so play the clip the moment it lands instead of
      // dropping to a tone — otherwise the opening "inn" is always a beep.
      const pending = this.voiceLoads[clip];
      if (pending) {
        void pending.then(() => {
          const late = this.voiceBuffers[clip];
          if (late && this.ctx) this.playVoice(late);
        });
        return;
      }
      // no clip and no load in flight → fall through to the tone
    }

    const freq = cueFrequency(kind);
    if (freq <= 0) return; // empty hold: silent
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(g);
    g.connect(this.cueBus ?? this.master);
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.2, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
    osc.start(now);
    osc.stop(now + 0.5);
  }

  // Soft per-second counting tick.
  tick(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 1100;
    osc.connect(g);
    g.connect(this.cueBus ?? this.master);
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.15, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0005, now + 0.05);
    osc.start(now);
    osc.stop(now + 0.06);
  }

  // Distinct soft tone: the "about to change" early warning.
  changeTone(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 587.33; // D5, warmer than the ticks
    osc.connect(g);
    g.connect(this.cueBus ?? this.master);
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.13, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    osc.start(now);
    osc.stop(now + 0.32);
  }

  // Hold a steady, gentle bed (guided non-breath phases). level 0 mutes it.
  ambient(level: number): void {
    if (!this.ctx || !this.beatGain) return;
    const now = this.ctx.currentTime;
    const g = this.beatGain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(level * this.bedScale, now + 1.5);
    if (this.leftOsc && this.rightOsc) {
      this.rampFrequency(this.leftOsc.frequency, this.carrierHz, now);
      this.rampFrequency(this.rightOsc.frequency, this.carrierHz + this.beatHz, now);
    }
  }

  settleDrone(settleBeatHz?: number): void {
    if (!this.ctx || !this.beatGain) return;
    const now = this.ctx.currentTime;
    const g = this.beatGain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0.12, now + 1.5);

    // No breath during the settle: rest the pitch back at the base carrier.
    if (this.leftOsc && this.rightOsc) {
      this.rampFrequency(this.leftOsc.frequency, this.carrierHz, now);
      // Ease the beat to its settle value (or hold the base beat) over 30s.
      const targetBeat = settleBeatHz ?? this.beatHz;
      this.rightOsc.frequency.cancelScheduledValues(now);
      this.rightOsc.frequency.setValueAtTime(this.rightOsc.frequency.value, now);
      this.rightOsc.frequency.linearRampToValueAtTime(this.carrierHz + targetBeat, now + 30);
    }
  }

  // Fade the master gain down, then suspend the context so the binaural bed,
  // nature loop, and cues all freeze together. The `paused` flag lets a resume
  // that arrives before the fade completes cancel the pending suspend.
  pause(): void {
    if (!this.ctx || !this.master) return;
    this.paused = true;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const g = this.master.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0.0001, now + 0.25);
    if (this.suspendTimer !== null) window.clearTimeout(this.suspendTimer);
    this.suspendTimer = window.setTimeout(() => {
      this.suspendTimer = null;
      if (this.paused) void ctx.suspend().catch(() => {});
    }, 300);
  }

  // Resume the context and fade the master gain back up to the run volume.
  resume(): void {
    if (!this.ctx || !this.master) return;
    this.paused = false;
    if (this.suspendTimer !== null) {
      window.clearTimeout(this.suspendTimer);
      this.suspendTimer = null;
    }
    const ctx = this.ctx;
    const master = this.master;
    void ctx
      .resume()
      .then(() => {
        if (this.paused) return; // a newer pause arrived while resume() was in flight
        const now = ctx.currentTime;
        const g = master.gain;
        g.cancelScheduledValues(now);
        g.setValueAtTime(g.value, now);
        g.linearRampToValueAtTime(this.volume, now + 0.25);
      })
      .catch(() => {});
  }

  stop(): void {
    this.paused = false;
    if (this.suspendTimer !== null) {
      window.clearTimeout(this.suspendTimer);
      this.suspendTimer = null;
    }
    try {
      this.leftOsc?.stop();
      this.rightOsc?.stop();
      // Detach this segment's graph from the destination. Required when the
      // context is shared across a run, so masters don't accumulate.
      this.master?.disconnect();
    } catch {
      /* already stopped */
    }
    this.leftOsc = undefined;
    this.rightOsc = undefined;
    this.beatGain = undefined;
    this.master = undefined;
    this.cueBus = undefined;
    this.voiceBuffers = {};
    this.voiceLoads = {};
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = null;
    }
    if (this.ctx) {
      // Only close a context we created ourselves. A shared run-level context
      // outlives each segment and is closed by its owner (ProtocolPlayer).
      if (this.ownsCtx) void this.ctx.close().catch(() => {});
      this.ctx = null;
    }
  }
}
